import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { alpha, Button, Grid, Stack, Typography, styled } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type ViewProductStore from '../../AppData/_stores/ViewProductStore';
import ProductCatalogCard from '../Card/ProductCatalogCard';
import type { Props as ProductCatalogCardProps } from '../Card/ProductCatalogCard';
import useProductCatalogActions from '../hooks/useProductCatalogActions';
import useProductCatalogFiltering from '../hooks/useProductCatalogFiltering';
import useProductCatalogProducts, { type ProductCatalogProductEntry } from '../hooks/useProductCatalogProducts';

const productCardHoverColors = ['#00aced', '#ea008a', '#e7d236', '#ea008a'];
const INITIAL_RENDERED_PRODUCT_COUNT = 48;
const RENDERED_PRODUCT_INCREMENT = 24;

type ProductCatalogResultsLayout = 'catalog' | 'lab';

const ProductGridItem = styled(Grid, {
  shouldForwardProp: prop => prop !== 'resultsLayout',
})<{ resultsLayout: ProductCatalogResultsLayout }>(({ resultsLayout }) => ({
  display: 'flex',

  ...(resultsLayout === 'lab' ? {
    '@media (min-width:1130px)': {
      flexBasis: '33.333333%',
      maxWidth: '33.333333%',
    },

    '@media (min-width:1430px)': {
      flexBasis: '25%',
      maxWidth: '25%',
    },
    '@media (min-width:1730px)': {
      flexBasis: '20%',
      maxWidth: '20%',
    },
    '@media (min-width:2030px)': {
      flexBasis: '16.666666%',
      maxWidth: '16.666666%',
    },
  } : {
    '@media (min-width:1200px)': {
      flexBasis: '25%',
      maxWidth: '25%',
    },

    '@media (min-width:1630px)': {
      flexBasis: '20%',
      maxWidth: '20%',
    },
    '@media (min-width:1930px)': {
      flexBasis: '16.666666%',
      maxWidth: '16.666666%',
    },
  }),
}));

const EmptyProductsState = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  border: '1px dashed ' + alpha(theme.palette.common.black, 0.18),
  borderRadius: 8,
  color: theme.palette.text.secondary,
  minHeight: 280,
  padding: theme.spacing(5, 2),
  textAlign: 'center',
}));

const LoadMoreSection = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  padding: theme.spacing(1, 0, 2),
}));

const getProductCardHoverColor = (productId: number) => {
  return productCardHoverColors[productId % productCardHoverColors.length];
}

type ProductCatalogResultCardProps = Partial<Omit<ProductCatalogCardProps, 'hoverColor' | 'product'>>;

interface ProductCatalogResultsProps {
  getCardProps?: (product: ViewProductStore) => ProductCatalogResultCardProps
  layout?: ProductCatalogResultsLayout
}

const ProductCatalogGridItem = memo(function ProductCatalogGridItem(props: {
  getCardProps?: (product: ViewProductStore) => ProductCatalogResultCardProps
  layout: ProductCatalogResultsLayout
  product: ViewProductStore
}) {
  return <ProductGridItem
    item
    resultsLayout={props.layout}
    xs={6}
    sm={props.layout === 'lab' ? 4 : 6}
    md={props.layout === 'lab' ? 6 : 4}
  >
    <ProductCatalogCard
      product={props.product}
      hoverColor={getProductCardHoverColor(props.product.get('id'))}
      {...props.getCardProps?.(props.product)}
    />
  </ProductGridItem>
});

export default function ProductCatalogResults(props: ProductCatalogResultsProps) {
  const [t] = useTranslation('products');
  const layout = props.layout ?? 'catalog';
  const loadMoreAnchorRef = useRef<HTMLDivElement | null>(null);
  const {
    clearFilters,
  } = useProductCatalogActions();
  const {
    hasActiveFilters,
  } = useProductCatalogFiltering();
  const {
    visibleProductEntries,
  } = useProductCatalogProducts();
  const [renderedProductLimit, setRenderedProductLimit] = useState(INITIAL_RENDERED_PRODUCT_COUNT);

  const renderedProductEntries = useMemo(() => {
    return visibleProductEntries.slice(0, renderedProductLimit);
  }, [renderedProductLimit, visibleProductEntries])
  const hasMoreProducts = renderedProductLimit < visibleProductEntries.length;
  const hasVisibleProducts = visibleProductEntries.length > 0;

  const renderViewProduct = useCallback(([, product]: ProductCatalogProductEntry) => {
    return <ProductCatalogGridItem
      key={product.get('id')}
      getCardProps={props.getCardProps}
      layout={layout}
      product={product}
    />
  }, [layout, props.getCardProps])

  const onLoadMoreProducts = useCallback(() => {
    setRenderedProductLimit(limit => Math.min(limit + RENDERED_PRODUCT_INCREMENT, visibleProductEntries.length));
  }, [visibleProductEntries.length])

  useEffect(() => {
    setRenderedProductLimit(Math.min(INITIAL_RENDERED_PRODUCT_COUNT, visibleProductEntries.length));
  }, [visibleProductEntries])

  useEffect(() => {
    if (!hasMoreProducts || loadMoreAnchorRef.current === null || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        onLoadMoreProducts();
      }
    }, {
      rootMargin: '900px 0px',
    });

    observer.observe(loadMoreAnchorRef.current);

    return () => observer.disconnect();
  }, [hasMoreProducts, onLoadMoreProducts])

  return <>
    {hasVisibleProducts ? renderedProductEntries.map(renderViewProduct) : <Grid item xs={12}>
      <EmptyProductsState spacing={2}>
        <SearchOffRoundedIcon sx={{ color: 'secondary.light', fontSize: 42 }} />
        <Stack spacing={0.75} alignItems="center">
          <Typography variant="h3" color="secondary.main">{t('No products found')}</Typography>
          <Typography variant="body1" sx={{ maxWidth: 440 }}>
            {t('Try removing a filter or changing your search.')}
          </Typography>
        </Stack>
        {hasActiveFilters ? <Button
          color="secondary"
          size="medium"
          variant="outlined"
          onClick={clearFilters}
        >{t('Clear filters')}</Button> : null}
      </EmptyProductsState>
    </Grid>}
    {hasMoreProducts ? <Grid item xs={12}>
      <LoadMoreSection ref={loadMoreAnchorRef}>
        <Button
          color="secondary"
          size="large"
          startIcon={<ExpandMoreRoundedIcon />}
          variant="outlined"
          onClick={onLoadMoreProducts}
        >{t('Load more products')}</Button>
      </LoadMoreSection>
    </Grid> : null}
  </>;
}
